import type { JSX } from 'react';

import { Sinnbild } from './Sinnbild';

/**
 * Die aktiven Filter einer Liste als abwählbare Marken (Pflichtenheft 2.9,
 * Listenmuster).
 *
 * WARUM SIE ÜBERHAUPT DASTEHEN: Eine Liste, die aus zwölf Zeilen besteht,
 * beantwortet nicht, ob es zwölf gibt oder ob ein Filter die übrigen
 * verbirgt. Die Marken sind die Antwort, und weil sie zugleich der Weg
 * zurück sind, kostet das Zurücknehmen einen Klick statt der Suche nach dem
 * Auswahlfeld, das man vor zehn Minuten gesetzt hat.
 *
 * Sie standen bis hierher viermal als dieselben zwölf Zeilen in vier Listen —
 * und in zwei Listen gar nicht. Beides ist derselbe Fehler.
 */

export type Filtermarke = {
  readonly text: string;
  /**
   * Der Feldname, wo der Text ihn nicht nennt („Kundenart"). Er steht nur für
   * Vorlesehilfen vor dem Text (Auftrag CRM-59): Die Liste zeigt „Privat",
   * vorgelesen wird „Kundenart: Privat entfernen". Nur setzen, wo die Liste
   * eine einzige Filterart hat — bei mehreren gehört der Feldname sichtbar in
   * den Text.
   */
  readonly feld?: string;
  readonly entfernen: () => void;
};

/**
 * DIE ABWÄHLBARE MARKE ALS BEDIENELEMENT (Auftrag CRM-59, ÄA-49). Sie steht
 * in der Formsprache der Knöpfe — Höhe, Radius, Schrift —, das Kreuz als
 * Sinnbild rechts. Eine Form für alle Stellen, an denen man einen Eintrag
 * aus einer Menge wegklickt: aktive Filter, Standorte eines Produkts,
 * zugeteilte Ressourcen. Zustandsmarken (gut, warn, krit) bleiben `.marke`.
 */
export function AbwaehlbareMarke({
  text,
  feld,
  inaktiv = false,
  titel,
  entfernen,
}: {
  readonly text: string;
  readonly feld?: string;
  /** Ein Eintrag, der nicht mehr gilt (abgeschalteter Standort): ruhige Schrift. */
  readonly inaktiv?: boolean;
  readonly titel?: string;
  readonly entfernen: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={inaktiv ? 'filtermarke filtermarke--inaktiv' : 'filtermarke'}
      onClick={entfernen}
      {...(titel === undefined ? {} : { title: titel })}
    >
      {/*
        Die Leerzeichen stehen zwischen den Spannen und nicht in ihnen: Sonst
        fallen sie bei der Namensbildung weg, und vorgelesen wird
        „Kundenart:Privatentfernen".
      */}
      {feld !== undefined && <span className="nur-vorlesen">{feld}:</span>} {text}{' '}
      {/*
        Das Kreuz ist für eine Vorlesehilfe stumm; die Handlung steht
        stattdessen als Wort dahinter. Ohne das läse sie „Status:
        versendet ד vor.
      */}
      <Sinnbild gewicht="light" name="x" /> <span className="nur-vorlesen">entfernen</span>
    </button>
  );
}

export function Filtermarken({
  marken,
}: {
  readonly marken: readonly Filtermarke[];
}): JSX.Element | null {
  // Keine leere Leiste: Wo nichts gefiltert ist, steht auch nichts.
  if (marken.length === 0) return null;

  return (
    <ul className="liste__marken" aria-label="Aktive Filter">
      {marken.map((m) => (
        <li key={`${m.feld ?? ''}${m.text}`}>
          <AbwaehlbareMarke
            text={m.text}
            entfernen={m.entfernen}
            {...(m.feld === undefined ? {} : { feld: m.feld })}
          />
        </li>
      ))}
    </ul>
  );
}
